/*
 * Pointelle embroidery selector on the product page.
 *
 * Desktop: an "Add an icon" checkbox above the main Add to Cart opens the icon picker.
 * Mobile: a green "Add an icon" button below Add to Cart opens the same picker.
 * Unticking clears the icon and restores the product photos.
 *
 * The main Add to Cart is the only purchase action. When an icon is chosen and its
 * terms accepted, this file adds the embroidery line item properties to that form;
 * assets/product-form.js adds the garment as usual and then the linked fee line
 * (assets/pointelle-orders.js).
 */
if (!customElements.get('pointelle-customizer')) {
  const DEFAULT_DISCLAIMER = 'This is a custom order. Expect two weeks to make it and it is non-returnable.';
  const DEFAULT_STRINGS = {
    addIcon: 'Add an icon',
    selectedIcon: 'Selected icon:',
    chooseIcon: 'Choose an embroidered icon',
    selectIconError: 'Choose an icon, or untick “Add an icon” to add the garment without one.',
    acceptTermsError: 'Check the box to accept the custom-order terms.',
    feeError: 'The embroidery could not be added, so your garment was not added either. Please try again.',
  };

  class PointelleCustomizer extends HTMLElement {
    lifecycle = new AbortController();
    ready = Promise.resolve();
    cleanup = null;

    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.ready = this.setup().catch((error) => {
        console.error('Pointelle preview:', error);
        this.textContent = 'Embroidery preview could not load. Please refresh.';
      });
    }

    disconnectedCallback() {
      this.lifecycle.abort();
      this.cleanup?.();
    }

    readJSON(selector) {
      const element = this.querySelector(selector);
      return element ? JSON.parse(element.textContent) : null;
    }

    async setup() {
      const root = this;
      const { signal } = this.lifecycle;
      const scrollBehavior = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

      let config = this.readJSON('[data-pointelle-config]');
      const hasInlineConfig = Boolean(config);
      if (!config) {
        const response = await fetch(root.dataset.config);
        if (!response.ok) throw new Error('Configuration unavailable');
        config = await response.json();
      }

      const globalSettings = document.getElementById('pointelle-global-settings');
      if (!hasInlineConfig && globalSettings) Object.assign(config, JSON.parse(globalSettings.textContent));

      const strings = { ...DEFAULT_STRINGS, ...this.readJSON('[data-pointelle-strings]') };
      if (window.PointelleOrders) window.PointelleOrders.messages = { feeError: strings.feeError };

      const product = config.products.find((item) => item.id === root.dataset.product);
      if (!product) return;

      // Tanks sit the icon tighter under the binding than tees and long sleeves, so a garment
      // placement can override the neckline gap each icon carries. Empty keeps the icon's own gap.
      const iconGapIn = (icon) => product.iconGapIn ?? icon.offsetIn;

      const asset = (name) => root.dataset.asset.replace('pointelle-embroidered-espresso-martini.png', name);
      const iconURL = (icon) => icon.imageURL || asset(`pointelle-embroidered-${icon.id}.png`);
      // Canvas thumbnail only. 600px is 70% smaller than the 2000px artwork URL.
      const canvasIconURL = (icon) => {
        const url = new URL(iconURL(icon), location.origin);
        url.searchParams.set('width', '600');
        return url.href;
      };
      const html = (value) =>
        String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
      const photoURL = (item) => {
        const url = new URL(item.sourceImages[item.imageIndex - 1].url, location.origin);
        url.searchParams.set('width', '1200');
        return url.href;
      };

      let offsets = config.photoOffsets || {};
      if (!config.backend) {
        try {
          const response = await fetch(asset('pointelle-offsets.json'));
          offsets = response.ok ? await response.json() : {};
        } catch {
          offsets = {};
        }
      }

      const defaultOffset = { x: 0, y: 0, skew: 0, perspective: 0, ...offsets[product.handle] };
      const offsetKey = `pf-pointelle-offset-v1:${product.id}`;
      let photoOffset = { ...defaultOffset };
      try {
        const saved = JSON.parse(localStorage.getItem(offsetKey));
        if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
          photoOffset = {
            x: Math.max(-80, Math.min(80, saved.x)),
            y: Math.max(-80, Math.min(80, saved.y)),
            skew: Math.max(-30, Math.min(30, Number(saved.skew) || 0)),
            perspective: Math.max(-40, Math.min(40, Number(saved.perspective) || 0)),
          };
        }
      } catch {}

      // An Icon gap fully describes the vertical placement from the neck point, so the preview
      // Y nudge (backend or browser-local) is not applied to that garment. X, skew and perspective still are.
      const appliedOffsetY = () => (product.iconGapIn == null ? photoOffset.y : 0);

      const params = new URLSearchParams(location.search);
      const mode = params.get('icon_concept') === 'modal' ? 'modal' : 'inline';

      // Only a browser refresh clears the picker. Color and size changes reuse this page.
      const reloaded = performance.getEntriesByType('navigation')[0]?.type === 'reload';
      const clearOnRefresh = reloaded && !window.pointelleRefreshCleared;
      if (clearOnRefresh) window.pointelleRefreshCleared = true;

      let isOpen = false;
      let selected = null;
      if (clearOnRefresh) {
        if (params.has('customize') || params.has('icon')) {
          const url = new URL(location.href);
          url.searchParams.delete('customize');
          url.searchParams.delete('icon');
          history.replaceState(history.state, '', url);
        }
      } else if (params.get('customize') === '1') {
        isOpen = true;
        selected = config.icons.find((icon) => icon.id === params.get('icon')) || null;
      }

      const saveState = () => {
        const url = new URL(location.href);
        if (isOpen) {
          url.searchParams.set('customize', '1');
        } else {
          url.searchParams.delete('customize');
        }
        if (isOpen && selected) {
          url.searchParams.set('icon', selected.id);
        } else {
          url.searchParams.delete('icon');
        }
        history.replaceState(history.state, '', url);
      };

      const form = document.getElementById(root.dataset.form);
      const gallery = document.querySelector('media-gallery');
      const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
      const photo = photoURL(product);
      const panelId = `pi-panel-${product.id}`;
      const errorId = `pi-error-${product.id}`;

      // The visible checkbox, icon row, and messages are rendered in Liquid so they paint
      // with the product form. This builds them only when that markup is absent.
      if (!root.querySelector('.pi-toggle')) {
        root.insertAdjacentHTML(
          'afterbegin',
          `<label class="pi-toggle">
            <input type="checkbox" class="pi-toggle__input" aria-controls="${panelId}">
            <span class="pi-toggle__text"></span>
          </label>`
        );
      }
      if (!root.querySelector('.pi-panel')) {
        root.insertAdjacentHTML(
          'beforeend',
          `<div class="pi-panel" id="${panelId}" hidden>
            <p class="pi-selected-label" aria-live="polite"></p>
            <div class="pi-mobile-preview"></div>
            <div class="pi-icon-strip">
              <div class="pi-choices" role="group"></div>
            </div>
            <label class="pi-ack">
              <input type="checkbox" class="pi-ack__input">
              <span class="pi-ack__text"></span>
            </label>
          </div>`
        );
      }
      if (!root.querySelector('.pi-error')) {
        root.insertAdjacentHTML(
          'beforeend',
          `<p class="pi-error" id="${errorId}" role="alert"></p><p class="pi-status" role="status"></p>`
        );
      }

      const toggleInput = root.querySelector('.pi-toggle__input');
      const panel = root.querySelector('.pi-panel');
      const selectedLabel = root.querySelector('.pi-selected-label');
      const mobilePreview = root.querySelector('.pi-mobile-preview');
      const strip = root.querySelector('.pi-icon-strip');
      const choices = root.querySelector('.pi-choices');
      const ack = root.querySelector('.pi-ack__input');
      const error = root.querySelector('.pi-error');
      const status = root.querySelector('.pi-status');

      root.querySelector('.pi-toggle__text').textContent = strings.addIcon;
      root.querySelector('.pi-ack__text').textContent = config.disclaimer || DEFAULT_DISCLAIMER;
      choices.setAttribute('aria-label', strings.chooseIcon);

      // Mobile-only checkbox, placed directly below the main Add to Cart.
      // Checking it opens the picker above Add to Cart and scrolls that block into view.
      let open = document.getElementById(`pi-open-${product.id}`);
      if (!open || open.tagName === 'BUTTON') {
        const label = document.createElement('label');
        label.id = `pi-open-${product.id}`;
        label.className = 'pi-open';
        label.setAttribute('aria-expanded', 'false');
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.className = 'pi-open__input';
        const text = document.createElement('span');
        text.className = 'pi-open__text';
        label.append(box, text);
        if (open) open.replaceWith(label);
        else (form?.closest('product-form') || root).after(label);
        open = label;
      }
      const openInput = open.querySelector('.pi-open__input');
      openInput?.setAttribute('aria-controls', panelId);
      const openText = open.querySelector('.pi-open__text');
      if (openText) openText.textContent = strings.addIcon;

      const productInfo = root.closest('product-info');
      productInfo?.classList.add('pi-pointelle-product');
      let topColors = productInfo?.querySelector('.pi-top-colors');
      let colors = topColors?.querySelector('.pi-colors');
      if (!topColors) {
        colors = document.createElement('div');
        colors.className = 'pi-colors';
        colors.setAttribute('role', 'group');
        colors.setAttribute('aria-label', 'Choose pointelle color');

        const colorLabel = document.createElement('span');
        colorLabel.className = 'pi-color-label';
        colorLabel.textContent = `Color: ${product.color}`;

        for (const item of config.products.filter((candidate) => candidate.family === product.family)) {
          const link = document.createElement('a');
          link.setAttribute('aria-label', item.color);
          link.title = item.color;
          link.style.setProperty('--pi-color', item.hex);
          link.dataset.handle = item.handle;
          if (item.id === product.id) link.setAttribute('aria-current', 'true');
          colors.append(link);
        }

        topColors = document.createElement('div');
        topColors.className = 'pi-top-colors';
        topColors.append(colorLabel, colors);
        const variantPicker = productInfo?.querySelector('variant-selects');
        if (variantPicker) {
          variantPicker.before(topColors);
        } else {
          root.before(topColors);
        }
      }
      topColors.hidden = root.dataset.swatchesEnabled === 'false';

      const matchingVariant = (handle) => {
        const size = productInfo?.querySelector('variant-selects input[type=radio]:checked')?.value;
        return config.products.find((item) => item.handle === handle)?.variants.find((variant) => variant.size === size);
      };

      const colorUrl = (handle) => {
        const url = new URL(`/products/${handle}`, location.origin);
        url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
        url.searchParams.set('icon_concept', mode);
        if (isOpen) {
          url.searchParams.set('customize', '1');
          url.searchParams.set('icon', selected?.id || 'none');
        }
        const variant = matchingVariant(handle);
        if (variant) url.searchParams.set('variant', variant.id);
        return url.href;
      };

      const pageCache = window.pointellePageCache || (window.pointellePageCache = new Map());
      const loadProduct = (handle) => {
        const variant = matchingVariant(handle);
        const key = `${handle}:${variant?.id || ''}`;
        if (!pageCache.has(key)) {
          const url = new URL(`/products/${handle}`, location.origin);
          if (variant) url.searchParams.set('variant', variant.id);
          const request = fetch(url, { credentials: 'same-origin' })
            .then((response) => {
              if (!response.ok) throw new Error('Product unavailable');
              return response.text();
            })
            .catch((requestError) => {
              pageCache.delete(key);
              throw requestError;
            });
          pageCache.set(key, request);
        }
        return pageCache.get(key);
      };

      let switching = false;
      const switchProduct = async (handle) => {
        if (switching || handle === product.handle) return;
        switching = true;

        const url = colorUrl(handle);
        root.setAttribute('aria-busy', 'true');
        status.textContent = 'Loading your pointelle…';
        const submit = form?.querySelector('[type=submit]');
        if (submit) submit.disabled = true;

        try {
          const nextDocument = new DOMParser().parseFromString(await loadProduct(handle), 'text/html');
          const next = nextDocument.querySelector('main');
          if (!next?.querySelector('pointelle-customizer') || typeof HTMLUpdateUtility === 'undefined') {
            throw new Error('Full navigation required');
          }

          history.pushState({ pointelleSwitch: true }, '', url);
          document.title = nextDocument.title;
          HTMLUpdateUtility.viewTransition(document.querySelector('main'), next, productInfo?.preProcessHtmlCallbacks, productInfo?.postProcessHtmlCallbacks);

          // The new page is inserted before this one, so the first customizer is the new one
          await document.querySelector('pointelle-customizer')?.ready;
          document.querySelector('.pi-top-colors')?.scrollIntoView({ block: 'start' });
        } catch (switchError) {
          location.assign(url);
        }
      };

      if (!window.pointelleHistoryListener) {
        window.pointelleHistoryListener = true;
        window.addEventListener('popstate', () => location.reload());
      }

      colors.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-handle]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        switchProduct(link.dataset.handle);
      });

      const prefetch = (event) => {
        const handle = event.target.closest('a[data-handle]')?.dataset.handle;
        if (handle) loadProduct(handle).catch(() => {});
      };
      colors.addEventListener('pointerover', prefetch);
      colors.addEventListener('focusin', prefetch);

      const warm = () => {
        if (!root.isConnected) return;
        for (const item of config.products.filter((candidate) => candidate.family === product.family && candidate.id !== product.id).slice(0, 3)) {
          loadProduct(item.handle).catch(() => {});
          const image = new Image();
          image.src = photoURL(item);
        }
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(warm, { timeout: 2000 });
      } else {
        setTimeout(warm, 500);
      }

      productInfo?.addEventListener(
        'change',
        (event) => {
          if (!event.target.matches('variant-selects input[type=radio]')) return;
          for (const link of colors.querySelectorAll('a')) link.href = colorUrl(link.dataset.handle);
          warm();
        },
        { signal }
      );

      // Placement calibration (browser-local). Hidden from shoppers, as agreed with Printfresh:
      // `.hidden` is display: none, so the "%" button is also out of the accessibility tree and
      // the tab order, and the adjustment panel it opens can never be shown.
      {
        const adjust = document.createElement('fieldset');
        adjust.className = 'pi-offset-controls';
        adjust.hidden = true;
        adjust.id = `pi-photo-offset-${product.id}`;

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'pi-offset-toggle hidden';
        toggle.textContent = '%';
        toggle.setAttribute('aria-label', 'Toggle preview placement adjustments');
        toggle.setAttribute('aria-controls', adjust.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.addEventListener('click', () => {
          adjust.hidden = !adjust.hidden;
          toggle.setAttribute('aria-expanded', String(!adjust.hidden));
        });

        adjust.innerHTML =
          '<legend>Preview placement adjustment</legend><p>For this garment photo only. Left/right and up/down; embroidery size stays unchanged.</p>' +
          '<label>X <input type="range" min="-80" max="80" step="1" data-axis="x"><output></output></label>' +
          '<label>Y <input type="range" min="-80" max="80" step="1" data-axis="y"><output></output></label>' +
          '<label>Skew <input type="range" min="-30" max="30" step="1" data-axis="skew"><output></output></label>' +
          '<label>Perspective <input type="range" min="-40" max="40" step="1" data-axis="perspective"><output></output></label>' +
          '<p>Perspective tapers the left or right edge for an angled garment. Skew slants the icon.</p>' +
          '<button type="button">Reset adjustment</button>' +
          '<p>Saved for this garment on this browser. Values are pixels in the 900px-wide reference photo.</p>';

        if (product.iconGapIn != null) {
          const vertical = adjust.querySelector('[data-axis="y"]');
          vertical.disabled = true;
          vertical.closest('label').insertAdjacentHTML(
            'afterend',
            `<p>Up/down comes from this garment's Icon gap (${product.iconGapIn} in) in Shopify admin, so Y is not applied here.</p>`
          );
        }

        const sync = () => {
          for (const input of adjust.querySelectorAll('input')) {
            const unit = ['x', 'y'].includes(input.dataset.axis) ? ' px' : input.dataset.axis === 'skew' ? '°' : '%';
            input.value = photoOffset[input.dataset.axis];
            input.nextElementSibling.textContent = input.value + unit;
          }
        };

        adjust.addEventListener('input', (event) => {
          if (!event.target.dataset.axis) return;
          photoOffset[event.target.dataset.axis] = Number(event.target.value);
          try {
            localStorage.setItem(offsetKey, JSON.stringify(photoOffset));
          } catch {}
          sync();
          render();
          schedulePreview();
        });

        adjust.querySelector('button').addEventListener('click', () => {
          photoOffset = { ...defaultOffset };
          try {
            localStorage.removeItem(offsetKey);
          } catch {}
          sync();
          render();
          schedulePreview();
        });

        sync();
        strip.after(toggle, adjust);
      }

      const clearError = () => {
        error.textContent = '';
        for (const field of [choices, ack]) {
          field.removeAttribute('aria-invalid');
          field.removeAttribute('aria-describedby');
        }
      };

      const showError = (message, field) => {
        error.textContent = message;
        if (field) {
          field.setAttribute('aria-describedby', errorId);
          if (field === ack) field.setAttribute('aria-invalid', 'true');
        }
        error.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
      };

      // A tile cut off by the strip edge should land fully in view. The strip snaps, so
      // the smallest move that still rests on a snap keeps the choice from sliding back out.
      const revealChoice = (button) => {
        const maxScroll = Math.max(0, choices.scrollWidth - choices.clientWidth);
        const viewLeft = choices.getBoundingClientRect().left;
        const contentLeft = (element) => element.getBoundingClientRect().left - viewLeft + choices.scrollLeft;
        const tileLeft = contentLeft(button);
        const tileRight = tileLeft + button.getBoundingClientRect().width;
        const minScroll = Math.max(0, tileRight - choices.clientWidth);
        const maxForTile = Math.min(maxScroll, tileLeft);
        const current = choices.scrollLeft;
        if (current >= minScroll - 1 && current <= maxForTile + 1) return;

        const snaps = [...choices.children].map((item) => Math.max(0, Math.min(maxScroll, contentLeft(item))));
        const fitting = snaps.filter((snap) => snap >= minScroll - 1 && snap <= maxForTile + 1);
        const candidates = fitting.length ? fitting : [maxForTile];
        const target = candidates.reduce((best, snap) => (Math.abs(snap - current) < Math.abs(best - current) ? snap : best));
        choices.scrollTo({ left: target, behavior: scrollBehavior() });
      };

      const bindIcon = (button, icon) => {
        button.addEventListener('click', () => {
          selected = selected?.id === icon.id ? null : icon;
          clearError();
          saveState();
          render();
          revealChoice(button);
          schedulePreview();
        });
      };

      const existingChoices = [...choices.querySelectorAll('button[data-icon]')];
      if (existingChoices.length) {
        for (const button of existingChoices) {
          const icon = config.icons.find((item) => item.id === button.dataset.icon);
          if (icon) bindIcon(button, icon);
        }
      } else {
        for (const icon of config.icons) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.icon = icon.id;
          button.setAttribute('aria-label', icon.name);
          button.title = icon.name;
          button.innerHTML = `<img src="${html(iconURL(icon))}" width="80" height="80" alt="" loading="lazy"><span class="pi-selected-x" aria-hidden="true">×</span>`;
          bindIcon(button, icon);
          choices.append(button);
        }
      }

      const nextIcons = document.createElement('button');
      nextIcons.type = 'button';
      nextIcons.className = 'pi-next-icons';
      nextIcons.setAttribute('aria-label', 'Scroll to more icons');
      nextIcons.innerHTML =
        '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
      strip.append(nextIcons);

      const prevIcons = nextIcons.cloneNode(true);
      prevIcons.className = 'pi-prev-icons';
      prevIcons.setAttribute('aria-label', 'Scroll to previous icons');
      strip.prepend(prevIcons);

      const syncScroll = () => {
        const fits = choices.scrollWidth <= choices.clientWidth + 2;
        prevIcons.hidden = fits;
        nextIcons.hidden = fits;
      };

      // Arrows loop: previous at the start goes to the end, next at the end returns to the start
      const scrollIcons = (direction) => {
        const end = choices.scrollWidth - choices.clientWidth;
        const atStart = direction < 0 && choices.scrollLeft <= 2;
        const atEnd = direction > 0 && choices.scrollLeft >= end - 2;
        if (atStart || atEnd) {
          choices.scrollTo({ left: direction > 0 ? 0 : end, behavior: scrollBehavior() });
        } else {
          choices.scrollBy({ left: direction * Math.max(100, choices.clientWidth * 0.75), behavior: scrollBehavior() });
        }
      };

      prevIcons.addEventListener('click', () => scrollIcons(-1));
      nextIcons.addEventListener('click', () => scrollIcons(1));
      window.matchMedia('(min-width: 750px)').addEventListener('change', syncScroll, { signal });
      choices.addEventListener('scroll', syncScroll, { passive: true, signal });
      const scrollResize = new ResizeObserver(syncScroll);
      scrollResize.observe(choices);
      signal.addEventListener('abort', () => scrollResize.disconnect());

      const scene = document.createElement('div');
      scene.className = 'pi-scene';
      scene.hidden = true;
      scene.innerHTML =
        '<div class="pi-scene-bar"><span>Your embroidered pointelle</span><button type="button" class="pi-original">Back to product photos</button></div>' +
        '<div class="pi-picture"></div>' +
        '<button type="button" class="pi-zoom">View embroidery up close ↗</button>';
      gallery?.prepend(scene);

      const dialog = document.createElement('dialog');
      dialog.className = 'pi-dialog';
      dialog.setAttribute('aria-label', 'Customize your pointelle with embroidery');
      dialog.innerHTML =
        '<button type="button" class="pi-dialog-close" aria-label="Close customization">Close</button>' +
        '<div class="pi-dialog-grid"><div class="pi-dialog-picture"></div><div class="pi-dialog-controls"></div></div>';
      document.body.append(dialog);

      this.cleanup = () => {
        dialog.remove();
        open.remove();
        scene.remove();
        topColors.remove();
      };

      const resetGallery = () => {
        scene.hidden = true;
        if (viewer) viewer.hidden = false;
      };

      const closeDialog = () => {
        dialog.close();
        root.querySelector('.pi-toggle').after(panel);
      };

      dialog.querySelector('.pi-dialog-close').addEventListener('click', closeDialog);
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });

      const removeFormProperties = () => {
        for (const input of form?.querySelectorAll('[data-pointelle-property]') || []) input.remove();
      };

      const loadImage = (src) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error('Preview image failed to load'));
          image.src = src;
        });

      // Flatten the same photo + icon placement the product page shows, then store a hosted URL.
      // Checkout replaces the thumbnail only when this URL is on a property named Preview.
      // Embroidery lines also carry _pointelle_pair, which keeps them out of the monogram fee.
      const createPreviewURL = async (icon, previewSignal) => {
        const geometry = icon.alphaGeometry;
        const iconScale = (icon.heightIn * product.ppi) / (geometry.maxY - geometry.minY);
        const iconWidth = geometry.width * iconScale;
        const iconHeight = geometry.height * iconScale;
        const [photoImage, iconImage] = await Promise.all([loadImage(photo), loadImage(canvasIconURL(icon))]);
        if (previewSignal?.aborted) throw new DOMException('Aborted', 'AbortError');

        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 750;
        const ctx = canvas.getContext('2d');
        const canvasScale = canvas.width / 900;

        ctx.scale(canvasScale, canvasScale);
        ctx.drawImage(photoImage, 0, 0, 900, 1125);
        ctx.translate(product.neckX + photoOffset.x, product.neckY + appliedOffsetY());
        ctx.rotate((-product.fabricCalibration.axisTiltDeg * Math.PI) / 180);
        ctx.translate(0, iconGapIn(icon) * product.ppi);
        ctx.translate((-(geometry.minX + geometry.maxX) * iconScale) / 2, -geometry.minY * iconScale);
        ctx.rotate((icon.rotation * Math.PI) / 180);
        ctx.transform(1, 0, Math.tan((photoOffset.skew * Math.PI) / 180), 1, 0, 0);

        const perspective = photoOffset.perspective / 100;
        if (!perspective) {
          ctx.drawImage(iconImage, -iconWidth / 2, -iconHeight / 2, iconWidth, iconHeight);
        } else {
          const strips = 64;
          const step = iconWidth / strips;
          for (let index = 0; index < strips; index++) {
            const factor = 1 + perspective * ((2 * (index + 0.5)) / strips - 1);
            ctx.drawImage(
              iconImage,
              (index / strips) * iconImage.width,
              0,
              iconImage.width / strips,
              iconImage.height,
              -iconWidth / 2 + index * step,
              (-iconHeight * factor) / 2,
              step + 0.2,
              iconHeight * factor
            );
          }
        }

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const response = await fetch('https://monogram-shopify-api-2bc142f90206.herokuapp.com/api/imagetolink', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ img_to_upload: dataUrl.split(',')[1] }),
          signal: previewSignal,
        });
        const result = await response.json();
        if (result.status !== 'success' || !result.data?.image_url) throw new Error('Preview upload failed');
        return result.data.image_url;
      };

      // Start the hosted preview once the shopper rests on one icon with the terms checked.
      // Fast icon changes only keep the last choice; an in-flight upload for an older icon is dropped.
      const PREVIEW_SETTLE_MS = 500;
      let previewTimer = 0;
      let previewGeneration = 0;
      let previewAbort = null;
      let preparedPreview = null;

      const dropPreparedPreview = () => {
        window.clearTimeout(previewTimer);
        previewTimer = 0;
        previewGeneration += 1;
        previewAbort?.abort();
        previewAbort = null;
        preparedPreview = null;
      };

      const beginPreview = (icon) => {
        const generation = previewGeneration;
        const abort = new AbortController();
        previewAbort = abort;
        preparedPreview = { iconId: icon.id, url: null, promise: null };
        const promise = createPreviewURL(icon, abort.signal)
          .then((url) => {
            if (generation !== previewGeneration) return null;
            preparedPreview.url = url;
            return url;
          })
          .catch((previewError) => {
            if (generation !== previewGeneration || previewError?.name === 'AbortError') return null;
            preparedPreview = null;
            console.error('Pointelle preview:', previewError);
            return null;
          });
        preparedPreview.promise = promise;
        return promise;
      };

      const schedulePreview = () => {
        window.clearTimeout(previewTimer);
        previewTimer = 0;
        if (!isOpen || !selected || !ack.checked) {
          dropPreparedPreview();
          return;
        }
        if (preparedPreview?.iconId === selected.id) return;

        const icon = selected;
        if (preparedPreview) dropPreparedPreview();
        previewTimer = window.setTimeout(() => {
          previewTimer = 0;
          if (!isOpen || selected?.id !== icon.id || !ack.checked || preparedPreview?.iconId === icon.id) return;
          beginPreview(icon);
        }, PREVIEW_SETTLE_MS);
      };

      const previewURLForSubmit = (icon) => {
        window.clearTimeout(previewTimer);
        previewTimer = 0;
        if (preparedPreview?.iconId === icon.id && preparedPreview.url) return preparedPreview.url;
        if (preparedPreview?.iconId === icon.id && preparedPreview.promise) return preparedPreview.promise;
        if (preparedPreview) dropPreparedPreview();
        return beginPreview(icon);
      };

      signal.addEventListener('abort', dropPreparedPreview);

      const addFormProperties = (previewURL) => {
        removeFormProperties();

        const iconArtwork = new URL(iconURL(selected), location.origin);
        iconArtwork.search = '';
        const properties = {
          'Select a Style': 'Icon',
          'Enter Text - 8 letters': selected.name,
          'Select Color': 'Icon',
          Preview: previewURL,
          _custom_order: config.disclaimer || DEFAULT_DISCLAIMER,
          _pointelle_pair: crypto.randomUUID(),
          _pointelle_icon: selected.id,
          _pointelle_role: 'garment',
          _pointelle_fee_variant: String(config.feeVariantId),
          _pointelle_image: iconArtwork.href,
          _pointelle_placement: JSON.stringify({
            slide: selected.slide,
            heightIn: selected.heightIn,
            offsetIn: iconGapIn(selected),
            rotation: selected.rotation,
            revision: '2026-09-09',
          }),
        };

        for (const [name, value] of Object.entries(properties)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          input.dataset.pointelleProperty = '';
          form.append(input);
        }
      };

      function art(closeup = false) {
        let overlay = '';
        if (selected) {
          const geometry = selected.alphaGeometry;
          const scale = (selected.heightIn * product.ppi) / (geometry.maxY - geometry.minY);
          const width = geometry.width * scale;
          const height = geometry.height * scale;
          // Fabric axis points down/right for positive tilt; SVG positive rotation points down/left.
          const angle = -product.fabricCalibration.axisTiltDeg;
          const iconImage = `<image href="${html(iconURL(selected))}" x="${-width / 2}" y="${-height / 2}" width="${width}" height="${height}"/>`;

          // Narrow SVG strips approximate a horizontal perspective projection on Safari as well as Chrome.
          let warped = iconImage;
          if (photoOffset.perspective) {
            warped = '';
            const strips = 64;
            const step = width / strips;
            const perspective = photoOffset.perspective / 100;
            for (let index = 0; index < strips; index++) {
              const x = -width / 2 + index * step;
              const factor = 1 + perspective * ((2 * (index + 0.5)) / strips - 1);
              warped += `<svg x="${x}" y="${(-height * factor) / 2}" width="${step + 0.015}" height="${height * factor}" viewBox="${x} ${-height / 2} ${step + 0.015} ${height}" preserveAspectRatio="none">${iconImage}</svg>`;
            }
          }

          overlay =
            `<g data-photo-offset="${photoOffset.x},${appliedOffsetY()}" transform="translate(${product.neckX + photoOffset.x} ${product.neckY + appliedOffsetY()}) rotate(${angle})">` +
            `<g transform="translate(0 ${iconGapIn(selected) * product.ppi})">` +
            `<g transform="translate(${(-(geometry.minX + geometry.maxX) * scale) / 2} ${-geometry.minY * scale}) rotate(${selected.rotation})">` +
            `<g data-photo-perspective="${photoOffset.perspective}" transform="skewX(${photoOffset.skew})">${warped}</g></g></g></g>`;
        }

        const crop = closeup ? `${product.neckX - 180} ${product.neckY - 90} 360 400` : '0 0 900 1125';
        const label = `${selected ? `${selected.name} embroidery on ` : ''}${product.title}`;
        return `<svg viewBox="${crop}" role="img" aria-label="${html(label)}"><image href="${html(photo)}" width="900" height="1125"/>${overlay}</svg>`;
      }

      function render() {
        for (const link of colors.querySelectorAll('a')) link.href = colorUrl(link.dataset.handle);
        mobilePreview.innerHTML = art(true);
        scene.querySelector('.pi-picture').innerHTML = art();
        dialog.querySelector('.pi-dialog-picture').innerHTML = art(true);

        for (const button of choices.children) {
          const icon = config.icons.find((item) => item.id === button.dataset.icon);
          const active = icon.id === selected?.id;
          button.setAttribute('aria-pressed', String(active));
          button.setAttribute('aria-label', active ? `Remove ${icon.name}` : icon.name);
        }

        selectedLabel.textContent = selected ? `${strings.selectedIcon} ${selected.name}` : strings.selectedIcon;
      }

      const setOpen = (nextOpen) => {
        isOpen = nextOpen;
        toggleInput.checked = isOpen;
        panel.hidden = !isOpen;
        open.setAttribute('aria-expanded', String(isOpen));
        if (openInput) openInput.checked = isOpen;
        root.classList.toggle('pi-customizer--open', isOpen);

        if (isOpen) {
          if (mode === 'modal') {
            dialog.querySelector('.pi-dialog-controls').append(panel);
            if (!dialog.open) dialog.showModal();
          } else {
            scene.hidden = false;
            if (viewer) viewer.hidden = true;
          }
        } else {
          // Unticking clears the icon and restores the product photos, so nothing is charged out of sight
          selected = null;
          ack.checked = false;
          clearError();
          if (dialog.open) closeDialog();
          resetGallery();
          removeFormProperties();
        }

        saveState();
        render();
        schedulePreview();
      };

      toggleInput.addEventListener('change', () => setOpen(toggleInput.checked));
      scene.querySelector('.pi-original').addEventListener('click', () => setOpen(false));

      openInput?.addEventListener('change', () => {
        if (!openInput.checked) {
          setOpen(false);
          return;
        }
        setOpen(true);
        toggleInput.focus({ preventScroll: true });
        root.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
      });

      ack.addEventListener('change', () => {
        clearError();
        schedulePreview();
      });

      scene.querySelector('.pi-zoom').addEventListener('click', () => {
        dialog.querySelector('.pi-dialog-controls').append(panel);
        panel.hidden = false;
        dialog.showModal();
        render();
      });

      const thumbList = gallery?.querySelector('[id^="Slider-Thumbnails"]');
      if (thumbList) {
        const item = document.createElement('li');
        item.className = 'pi-customize-thumb';
        const button = document.createElement('button');
        button.type = 'button';
        button.innerHTML = `<img src="${html(photo)}" alt="" width="90" height="112"><span>Customize +</span>`;
        button.addEventListener('click', () => setOpen(true));
        item.append(button);
        thumbList.insertBefore(item, thumbList.children[2] || null);
      }

      // Runs before product-form.js handles the submit (capture on document).
      // Blocks the add only when the box is ticked but the choice is incomplete.
      document.addEventListener(
        'submit',
        (event) => {
          if (event.target !== form) return;

          if (form.dataset.pointellePreviewReady === 'true') {
            // Keep the flag until every submit listener has seen this replay.
            queueMicrotask(() => {
              delete form.dataset.pointellePreviewReady;
            });
            return;
          }

          removeFormProperties();
          if (!isOpen) return;

          const stop = () => {
            event.preventDefault();
            event.stopImmediatePropagation();
          };

          if (!selected) {
            stop();
            showError(strings.selectIconError, choices);
            choices.querySelector('button')?.focus({ preventScroll: true });
            return;
          }

          if (!ack.checked) {
            stop();
            showError(strings.acceptTermsError, ack);
            ack.focus({ preventScroll: true });
            return;
          }

          if (!window.PointelleOrders?.addFee || !config.feeVariantId) {
            stop();
            showError(strings.feeError);
            return;
          }

          clearError();
          const icon = selected;
          const pendingPreview = previewURLForSubmit(icon);

          // The hosted photo is already stored. Put it on the form and let this same click add the garment.
          if (typeof pendingPreview === 'string') {
            addFormProperties(pendingPreview);
            return;
          }

          stop();

          const submitButton = form.querySelector('[type="submit"]');
          const spinner = submitButton?.querySelector('.loading__spinner');
          submitButton?.setAttribute('aria-disabled', 'true');
          submitButton?.classList.add('loading');
          spinner?.classList.remove('hidden');

          pendingPreview
            .then((previewURL) => {
              if (!previewURL || selected?.id !== icon.id) throw new Error('Preview upload failed');
              addFormProperties(previewURL);
              submitButton?.removeAttribute('aria-disabled');
              form.dataset.pointellePreviewReady = 'true';
              form.requestSubmit(submitButton);
            })
            .catch((error) => {
              console.error('Pointelle preview:', error);
              showError(strings.feeError);
              submitButton?.classList.remove('loading');
              submitButton?.removeAttribute('aria-disabled');
              spinner?.classList.add('hidden');
            });
        },
        { capture: true, signal }
      );

      setOpen(isOpen);
    }
  }

  customElements.define('pointelle-customizer', PointelleCustomizer);
}
